/**
 * WEB-3243 — картинка блока «Карточка» фоном под текстом
 * (`cardImageMode: 'background'`): когда фон включается, корпус карточки и
 * слои под текстом. Ветка `card` в `PublicBlockRendererCommon.tsx` зовёт эти
 * помощники, а сама решает только «фон или прежний вид» и светлый текст.
 *
 *  · фон — только при `background` И непустом `cardImageUrl`; без картинки
 *    карточка рисуется прежним видом (решение задания);
 *  · картинка на всю карточку с обрезкой (`object-fit: cover`; по центру —
 *    умолчание `object-position`), поверх — затемнение снизу вверх до
 *    середины высоты; текст — отдельным слоем у нижнего края;
 *  · «стекло» при фоне не действует: рамка — умолчание сплошной карточки,
 *    рамка автора (`resolveBlockBorder`) сохраняется, `overflow: hidden`
 *    приходит из корпуса прежнего вида и обрезает картинку по скруглению.
 */
import React from 'react';
import type { CSSProperties } from 'react';
import { resolveBlockBorder, type BlockBorderFields } from './blockBorder';

export function isCardImageBackdrop(block: {
  cardImageMode?: 'top' | 'background';
  cardImageUrl?: string;
}): boolean {
  return block.cardImageMode === 'background' && !!block.cardImageUrl;
}

/** Корпус карточки с фоном: прежний корпус + рамка сплошной + раскладка. */
export function cardBackdropShellStyle(
  base: CSSProperties,
  block: BlockBorderFields,
): CSSProperties {
  return {
    ...base,
    ...resolveBlockBorder({ ...block, cardVariant: 'solid' }),
    position: 'relative',
    minHeight: 200,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    // Тёмная подложка, пока картинка грузится или если не пришла: светлый
    // текст остаётся читаемым.
    background: '#1f1f1f',
  };
}

const layer: CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
};

/** Картинка на всю карточку и затемнение над ней (оба — под слоем текста). */
export function CardImageBackdrop({
  src,
}: {
  src: string;
}): React.ReactElement {
  return (
    <>
      <img
        src={src}
        alt=""
        decoding="async"
        style={{ ...layer, objectFit: 'cover' }}
      />
      <div
        style={{
          ...layer,
          background:
            'linear-gradient(to top, rgba(0,0,0,0.55), transparent 50%)',
        }}
      />
    </>
  );
}
